import React from "react";
import {
  DataTable,
  DataTableStateEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import { ParetoAnalysisData } from "../../../models/customer-analytics/customer-analytics.model";
import CallStatusCell from "../shared/call-status-cell";

interface ParetoDataTableProps {
  data: ParetoAnalysisData[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  sortField: string;
  sortOrder: 1 | -1;
  onPageChange: (event: DataTableStateEvent) => void;
  onSortChange: (field: string, order: string) => void;
  refreshData: () => void;
}

const getDecileColor = (decile: number): string => {
  const colors: Record<number, string> = {
    1: "#22c55e",
    2: "#4ade80",
    3: "#86efac",
    4: "#a3e635",
    5: "#facc15",
    6: "#fbbf24",
    7: "#fb923c",
    8: "#f87171",
    9: "#ef4444",
    10: "#dc2626",
  };
  return colors[decile] || "#94a3b8";
};

const ParetoDataTable: React.FC<ParetoDataTableProps> = (props) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const onSort = (e: DataTableSortEvent) => {
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    props.onSortChange(e.sortField, order);
  };

  const decileBody = (rowData: ParetoAnalysisData) => (
    <span
      className="ca-badge"
      style={{
        backgroundColor: getDecileColor(rowData.decile),
        color: rowData.decile <= 3 ? "#fff" : rowData.decile >= 8 ? "#fff" : "#1e293b",
      }}
    >
      D{rowData.decile}
    </span>
  );

  const cumulativeBody = (rowData: ParetoAnalysisData) => (
    <div className="flex align-items-center gap-2">
      <ProgressBar
        value={rowData.cumulativeRevenuePercent}
        showValue={false}
        style={{ height: "8px", width: "80px" }}
      />
      <span className="text-sm">{rowData.cumulativeRevenuePercent}%</span>
    </div>
  );

  const callDataBody = (rowData: ParetoAnalysisData) => (
    <CallStatusCell callDataJson={rowData.callData} />
  );

  return (
    <div className="col-12">
      <div className="card border-noround p-2">
        <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          totalRecords={props.totalRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          sortMode="single"
          sortField={props.sortField}
          sortOrder={props.sortOrder}
          onSort={onSort}
          scrollable
          scrollHeight="flex"
          responsiveLayout="scroll"
          emptyMessage="No records found"
          className="ca-datatable"
        >
          <Column field="customerName" header="Customer" />
          <Column field="primaryContactNbr" header="Phone Number" />
          <Column field="totalOrders" header="Orders" />
          <Column
            field="totalRevenue"
            header="Revenue"
            body={(row) => formatCurrency(row.totalRevenue)}
            sortable
          />
          <Column
            field="avgOrderValue"
            header="Avg Order"
            body={(row) => formatCurrency(row.avgOrderValue)}
            sortable
          />
          <Column
            field="daysSinceLastOrder"
            header="Days Inactive"
            sortable
          />
          <Column
            header="Cumulative %"
            body={cumulativeBody}
          />
          <Column header="Decile" body={decileBody} />
          <Column header="Last Call Status" body={callDataBody} style={{ minWidth: "140px" }} />
        </DataTable>
      </div>
    </div>
  );
};

export default ParetoDataTable;
